import { apiClient } from "shared/api/client/orval";

export type EntitlementStatus = "active" | "expired";

export type Entitlement = {
  id: string;
  title: string;
  date: string;
  status: EntitlementStatus;
  type: string;
  price: string;
  transactionId: string;
  expiresAt: string | null;
};

export type EntitlementsResponse = {
  storeConfigured: boolean;
  storeUnavailable: boolean;
  entitlements: Entitlement[];
};

type Rec = Record<string, unknown>;

const asRec = (v: unknown): Rec =>
  v && typeof v === "object" ? (v as Rec) : {};

const pick = (o: Rec, ...keys: string[]): unknown => {
  for (const k of keys) {
    if (o[k] !== undefined && o[k] !== null) return o[k];
  }
  return undefined;
};

const str = (v: unknown, fallback = ""): string =>
  v === undefined || v === null ? fallback : String(v);

function asList(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  const o = asRec(data);
  for (const key of ["entitlements", "Entitlements", "$values", "items", "data"]) {
    if (Array.isArray(o[key])) return o[key] as unknown[];
    if (o[key] && typeof o[key] === "object") return asList(o[key]);
  }
  return [];
}

function normalizeStatus(raw: unknown, expiresAt: string | null): EntitlementStatus {
  const value = str(raw).toLowerCase();
  if (value === "expired" || value === "revoked" || value === "inactive") return "expired";
  if (value === "active") return "active";
  if (expiresAt) {
    const expiry = Date.parse(expiresAt);
    if (!Number.isNaN(expiry) && expiry <= Date.now()) return "expired";
  }
  return "active";
}

export function normalizeEntitlement(raw: unknown, index = 0): Entitlement {
  const o = asRec(raw);
  const expiresRaw = pick(o, "expiresAt", "ExpiresAt", "expiresUtc", "ExpiresUtc");
  const expiresAt = expiresRaw === undefined ? null : str(expiresRaw) || null;
  const transactionId = str(
    pick(o, "transactionId", "TransactionId", "transaction_id", "licenseId", "LicenseId")
  );
  return {
    id: str(pick(o, "id", "Id"), transactionId || `entitlement-${index}`),
    title: str(pick(o, "title", "Title", "name", "Name"), "Untitled"),
    date: str(pick(o, "date", "Date", "issuedAt", "IssuedAt", "issuedUtc", "createdAt", "CreatedAt")),
    status: normalizeStatus(pick(o, "status", "Status"), expiresAt),
    type: str(pick(o, "type", "Type", "kind", "Kind"), "Entitlement"),
    price: str(pick(o, "price", "Price"), "—"),
    transactionId,
    expiresAt,
  };
}

export function normalizeEntitlementsResponse(raw: unknown): EntitlementsResponse {
  const o = asRec(raw);
  return {
    storeConfigured: Boolean(pick(o, "storeConfigured", "StoreConfigured") ?? false),
    storeUnavailable: Boolean(pick(o, "storeUnavailable", "StoreUnavailable") ?? false),
    entitlements: asList(raw).map((item, index) => normalizeEntitlement(item, index)),
  };
}

/** Live entitlements (screening licenses, published titles, store purchases). */
export async function getPaymentEntitlements(): Promise<EntitlementsResponse> {
  const res = await apiClient.get("/api/payment/entitlements");
  return normalizeEntitlementsResponse(res.data);
}

export type LicenseTier = "Personal" | "VenueScreening";

export type CheckoutRequest = {
  assetUploadId: number;
  licenseTier: LicenseTier;
};

/** Purchases a personal or venue license for a published title. */
export async function checkoutLicense(body: CheckoutRequest): Promise<Entitlement> {
  const res = await apiClient.post("/api/payment/checkout", body);
  const payload = asRec(res.data);
  return normalizeEntitlement(payload.entitlement ?? payload.Entitlement ?? res.data);
}
